import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from '@mui/icons-material/Star';
import * as React from "react";
import {useTranslation} from "react-i18next";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import Property from "crqlar-react-common/dist/components/Property";
import {MenuItem, Select, Stack} from "@mui/material";
import {EmailType} from "crqlar-react-common/dist/models/profile/email.model";

interface Props {
    data: UseEntityData<Profile>
}

const Emails = (props: Props) => {

    const {t} = useTranslation()
    const profile = props.data.item
    const setProfile = props.data.setItem
    const updateField = (field: keyof Profile, value: any) => {
        setProfile({...profile, [field]: value})
    }

    const fieldGroupGap = {xs: 3, sm: 2.5}
    const primaryTitle = (title: string) => (
        <Stack
            component={'span'}
            direction={'row'}
            gap={0.5}
            alignItems={'center'}
            sx={{display: 'inline-flex'}}
        >
            <StarIcon sx={{fontSize: '1em', lineHeight: 1, flexShrink: 0}}/>
            <span>{title}</span>
        </Stack>
    )

    return (
        <AdditionalProperties title={t('Emails')} variant={'outlined'} defaultExpanded={!!profile.emails?.length}>
            <Stack gap={fieldGroupGap}>
            {
                profile.emails?.map((email, index) => {
                    const provider = email.externalInfo?.[0]?.provider
                    const disabled = !!provider
                    return (
                        <Property
                            orientation={'vertical'}
                            key={index}
                            title={email.isPrimary ? primaryTitle(t('Primary email')) : t('Email') + ' ' + (index + 1)}
                            subtitle={provider}
                            sx={{
                                title: {
                                    mt: 2,
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            <Stack
                                gap={1}
                                width={'100%'}
                            >
                                <Property title={t('Email address')} labelAlignment={'top'}>
                                    <TextField
                                        id={`email-${index}`}
                                        data-testid={`email-${index}`}
                                        size={'small'}
                                        value={email.email}
                                        onChange={(e) => {
                                            const emails = [...(profile.emails || [])]
                                            emails[index] = {
                                                ...emails[index],
                                                email: e.target.value
                                            }
                                            updateField('emails', emails)
                                        }}
                                        fullWidth
                                        disabled={disabled}
                                    />
                                </Property>

                                <Property title={t('Email type')} labelAlignment={'top'}>
                                    <Select
                                        id={`email-type-${index}`}
                                        data-testid={`email-type-${index}`}
                                        variant={'outlined'}
                                        value={email.emailType}
                                        onChange={(e) => {
                                            const emails = [...(profile.emails || [])]
                                            emails[index] = {
                                                ...emails[index],
                                                emailType: e.target.value
                                            }
                                            updateField('emails', emails)
                                        }}
                                        size={'small'}
                                        disabled={disabled}
                                        fullWidth
                                    >
                                        {
                                            Object.keys(EmailType).map((type, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={type}
                                                    id={`email-type-${type}`}
                                                >
                                                    {type ? t(`Email type ${type}`) : t('(Not set)')}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </Property>

                                {
                                    !disabled && (
                                        <Stack
                                            direction={'row'}
                                            gap={1}
                                            justifyContent={'flex-end'}
                                            flexWrap={'wrap'}
                                        >
                                            {
                                                !email.isPrimary && (
                                                    <Button
                                                        variant={'text'}
                                                        size={'small'}
                                                        onClick={() => {
                                                            const emails = [...(profile.emails || [])]
                                                            emails.forEach(e => e.isPrimary = false)
                                                            emails[index] = {
                                                                ...emails[index],
                                                                isPrimary: true
                                                            }
                                                            updateField('emails', emails)
                                                        }}
                                                    >
                                                        {t('Set as primary')}
                                                    </Button>
                                                )
                                            }

                                            <Button
                                                variant={'text'}
                                                size={'small'}
                                                color={'error'}
                                                onClick={() => {
                                                    const emails = [...(profile.emails || [])]
                                                    emails.splice(index, 1)
                                                    updateField('emails', emails)
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </Stack>
                                    )
                                }
                            </Stack>

                        </Property>
                    )
                })
            }


            <Button
                startIcon={<AddIcon/>}
                onClick={() => {
                    const emails = [...(profile.emails || [])]
                    emails.push({email: '', isPrimary: false})
                    updateField('emails', emails)
                }}
                sx={{
                    alignSelf: 'flex-start'
                }}
            >
                {t('Add email')}
            </Button>

            </Stack>
        </AdditionalProperties>
    )
}

export default Emails;
